import React, { useState, useRef } from 'react';
import axios from 'axios';
import './ProfilePhotoUpload.css';

function ProfilePhotoUpload({ currentPhotoUrl, onPhotoUpdate }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(currentPhotoUrl);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setError('');
    setUploading(true);

    try {
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);

      // Upload to server
      const formData = new FormData();
      formData.append('photo', file);

      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`/api/upload/profile-photo`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('✅ Photo uploaded successfully');
      onPhotoUpdate(response.data.photoUrl);
      
    } catch (err) {
      console.error('❌ Error uploading photo:', err);
      setError(err.response?.data?.error || 'Failed to upload photo');
      setPreview(currentPhotoUrl); // Revert preview on error
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = async () => {
    if (!window.confirm('Are you sure you want to remove your profile photo?')) {
      return;
    }

    setUploading(true);
    setError('');

    try {
      const token = localStorage.getItem('accessToken');
      await axios.delete(`/api/upload/profile-photo`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('✅ Photo removed successfully');
      setPreview(null);
      onPhotoUpdate(null);
      
    } catch (err) {
      console.error('❌ Error removing photo:', err);
      setError(err.response?.data?.error || 'Failed to remove photo');
    } finally {
      setUploading(false);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="profile-photo-upload">
      <div className="photo-container">
        {preview ? (
          <img src={preview} alt="Profile" className="profile-photo" />
        ) : (
          <div className="profile-photo-placeholder">
            <span className="placeholder-icon">👤</span>
          </div>
        )}
        
        {uploading && (
          <div className="photo-overlay">
            <div className="spinner"></div>
          </div>
        )}
      </div>

      <div className="photo-actions">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          disabled={uploading}
        />
        
        <button
          type="button"
          onClick={handleClick}
          disabled={uploading}
          className="btn-upload"
        >
          {preview ? '📷 Change Photo' : '📷 Upload Photo'}
        </button>

        {preview && (
          <button
            type="button"
            onClick={handleRemovePhoto}
            disabled={uploading}
            className="btn-remove"
          >
            🗑️ Remove
          </button>
        )}
      </div>

      {error && <div className="photo-error">{error}</div>}
      
      <p className="photo-hint">
        Optional • Max 5MB • JPG, PNG, or GIF
      </p>
    </div>
  );
}

export default ProfilePhotoUpload;
